/* ============================================================================
   HEW³ 图表引擎（自研零依赖 SVG 渲染）
   由原型 assets/charts.js 移植，唯一改动是为每次调用分配唯一 SVG 渐变 / 滤镜 id。
   颜色参数沿用 CSS 变量（var(--h) 等），因为这些令牌现在由 @hew3/shared 注入到 :root，
   与原型的令牌体系完全一致，因此视觉效果与原型逐像素相同。
   由 lib/charts.ts 提供 TypeScript 类型封装。
   ============================================================================ */

/* ============================================================
   HEW³ · 轻量 SVG 图表引擎（零依赖）
   ============================================================ */
(function (root) {
  /* ---- 移植补丁：全局 id 唯一化 ----
     原型里 helix 的渐变 id 固定为 hg-h/hg-e/hg-w、滤镜为 hglow，spark 的渐变固定为 sg。
     一屏只渲染一个时安全，但在 React 里同页出现两个同类图表就会 id 冲突、渐变互相串色。
     此处为每次调用分配唯一后缀。 */
  var __uidSeq = 0;
  function __uid() { return '_' + (++__uidSeq); }
  'use strict';

  var NS = 'http://www.w3.org/2000/svg';

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ---------- 工具 ---------- */
  function pol(cx, cy, r, ang) {
    var a = (ang - 90) * Math.PI / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  }
  function poly(points) {
    return points.map(function (p) { return p[0].toFixed(2) + ',' + p[1].toFixed(2); }).join(' ');
  }

  /* ============================================================
     1. 三螺旋主视觉 —— H / E / W 三条螺旋交织
     ============================================================ */
  function helix(opts) {
    var o = Object.assign({ h: 78, e: 82, w: 92, syn: 84, w_: 354, h_: 126 }, opts || {});
    var W = o.w_, H = o.h_, cy = H / 2;
    var amp = 34, lambda = o.w_ / 1.35, phase = 0;
    var u = __uid();
    var strands = [
      { key: 'h', color: 'var(--h)', v: o.h, off: 0, name: 'H' },
      { key: 'e', color: 'var(--e)', v: o.e, off: 2.0944, name: 'E' },
      { key: 'w', color: 'var(--w)', v: o.w, off: 4.1888, name: 'W' }
    ];
    var s = '<svg viewBox="0 0 ' + W + ' ' + H + '" class="helix-svg" preserveAspectRatio="none">';
    s += '<defs>';
    strands.forEach(function (st) {
      s += '<linearGradient id="hg-' + st.key + u + '" x1="0" y1="0" x2="1" y2="0">' +
        '<stop offset="0%" stop-color="' + st.color + '" stop-opacity="0.15"/>' +
        '<stop offset="38%" stop-color="' + st.color + '" stop-opacity="0.95"/>' +
        '<stop offset="100%" stop-color="' + st.color + '" stop-opacity="0.35"/>' +
        '</linearGradient>';
    });
    s += '<filter id="hglow' + u + '"><feGaussianBlur stdDeviation="3.2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>';
    s += '</defs>';
    /* 螺旋线 */
    strands.forEach(function (st) {
      var d = '';
      for (var x = 12; x <= W - 12; x += 3) {
        var y = cy + amp * Math.sin((x / lambda) * 2 * Math.PI + st.off + phase);
        d += (x === 12 ? 'M' : 'L') + x.toFixed(1) + ' ' + y.toFixed(1);
      }
      s += '<path d="' + d + '" fill="none" stroke="url(#hg-' + st.key + u + ')" stroke-width="2.4" ' +
        'stroke-linecap="round" filter="url(#hglow' + u + ')" opacity="0.95">' +
        '<animate attributeName="stroke-dasharray" values="0 999;440 0" dur="2.4s" fill="freeze"/>' +
        '</path>';
    });
    /* 交织点 */
    for (var k = 0; k < 7; k++) {
      var x0 = 30 + k * ((W - 60) / 6);
      var y0 = cy + amp * Math.sin((x0 / lambda) * 2 * Math.PI);
      s += '<circle cx="' + x0.toFixed(1) + '" cy="' + y0.toFixed(1) + '" r="2.6" fill="#fff" opacity="0.5"/>';
    }
    /* 端点标签球 */
    strands.forEach(function (st, i) {
      var x = 20 + i * 0;
      var y = cy + amp * Math.sin((x / lambda) * 2 * Math.PI + st.off);
      s += '<circle cx="' + x.toFixed(1) + '" cy="' + y.toFixed(1) + '" r="9" fill="' + st.color + '" opacity="0.16"/>' +
        '<circle cx="' + x.toFixed(1) + '" cy="' + y.toFixed(1) + '" r="4.2" fill="' + st.color + '"/>';
      var xr = W - 20;
      var yr = cy + amp * Math.sin((xr / lambda) * 2 * Math.PI + st.off);
      s += '<circle cx="' + xr.toFixed(1) + '" cy="' + yr.toFixed(1) + '" r="4.2" fill="' + st.color + '"/>';
    });
    /* 中部协同指数 */
    s += '<g transform="translate(' + (W / 2) + ',' + cy + ')">';
    s += '<circle r="25" fill="rgba(5,8,15,0.82)" stroke="rgba(255,255,255,0.13)"/>';
    s += '<circle r="25" fill="none" stroke="url(#hg-h' + u + ')" stroke-width="1.4" stroke-dasharray="' +
      (2 * Math.PI * 25 * o.syn / 100).toFixed(1) + ' 999" transform="rotate(-90)" stroke-linecap="round"/>';
    s += '<text y="-1" text-anchor="middle" font-size="15" font-weight="600" fill="#E9EEF9" font-family="Bahnschrift,system-ui">' + o.syn + '</text>';
    s += '<text y="10" text-anchor="middle" font-size="7.5" fill="#7F8DA7" letter-spacing="0.6">协同指数</text>';
    s += '</g>';
    s += '</svg>';
    return s;
  }

  /* ============================================================
     2. 雷达图
     ============================================================ */
  function radar(opts) {
    var o = Object.assign({ axes: [], max: 100, size: 210, color: 'var(--h)', ref: null }, opts || {});
    var size = o.size, cx = size / 2, cy = size / 2, R = size / 2 - 34;
    var n = o.axes.length;
    var s = '<svg viewBox="0 0 ' + size + ' ' + size + '" width="100%" style="max-width:' + size + 'px;display:block;margin:0 auto">';
    /* 网格 */
    [0.25, 0.5, 0.75, 1].forEach(function (t) {
      var pts = [];
      for (var i = 0; i < n; i++) pts.push(pol(cx, cy, R * t, i * 360 / n));
      s += '<polygon points="' + poly(pts) + '" fill="none" stroke="rgba(255,255,255,0.075)" stroke-width="1"/>';
    });
    for (var i = 0; i < n; i++) {
      var p = pol(cx, cy, R, i * 360 / n);
      s += '<line x1="' + cx + '" y1="' + cy + '" x2="' + p[0].toFixed(1) + '" y2="' + p[1].toFixed(1) + '" stroke="rgba(255,255,255,0.06)"/>';
    }
    /* 参考（上一次/基准） */
    if (o.ref && o.ref.length) {
      var rp = o.ref.map(function (v, i) { return pol(cx, cy, R * Math.min(v / o.max, 1), i * 360 / n); });
      s += '<polygon points="' + poly(rp) + '" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="1.2" stroke-dasharray="4 3"/>';
    }
    /* 数据面 */
    var dp = o.axes.map(function (a, i) { return pol(cx, cy, R * Math.min(a.value / o.max, 1), i * 360 / n); });
    s += '<polygon points="' + poly(dp) + '" fill="' + o.color + '" fill-opacity="0.2" stroke="' + o.color + '" stroke-width="2" stroke-linejoin="round">' +
      '<animate attributeName="fill-opacity" values="0;0.2" dur="0.9s" fill="freeze"/></polygon>';
    dp.forEach(function (p) {
      s += '<circle cx="' + p[0].toFixed(1) + '" cy="' + p[1].toFixed(1) + '" r="3" fill="' + o.color + '" stroke="#0A1020" stroke-width="1.5"/>';
    });
    /* 轴标签 */
    o.axes.forEach(function (a, i) {
      var p = pol(cx, cy, R + 19, i * 360 / n);
      var anchor = Math.abs(p[0] - cx) < 6 ? 'middle' : (p[0] > cx ? 'start' : 'end');
      s += '<text x="' + p[0].toFixed(1) + '" y="' + (p[1] + 3.5).toFixed(1) + '" text-anchor="' + anchor +
        '" font-size="10" fill="#8A97AD">' + esc(a.label) + '</text>';
      var p2 = pol(cx, cy, R + 19, i * 360 / n);
      s += '<text x="' + p2[0].toFixed(1) + '" y="' + (p2[1] + 14).toFixed(1) + '" text-anchor="' + anchor +
        '" font-size="10.5" font-weight="600" fill="' + o.color + '" font-family="Bahnschrift,system-ui">' + a.value + '</text>';
    });
    s += '</svg>';
    return s;
  }

  /* ============================================================
     3. 折线 / 迷你趋势
     ============================================================ */
  function spark(opts) {
    var o = Object.assign({ data: [], w: 300, h: 56, color: 'var(--h)', fill: true, dots: false }, opts || {});
    var d = o.data, n = d.length;
    if (n < 2) return '';
    var min = Math.min.apply(null, d), max = Math.max.apply(null, d);
    var pad = (max - min) * 0.28 || 1;
    min -= pad; max += pad;
    var W = o.w, H = o.h, px = 4;
    var u = __uid();
    function X(i) { return px + i * (W - px * 2) / (n - 1); }
    function Y(v) { return H - px - (v - min) / (max - min) * (H - px * 2); }
    var line = '';
    for (var i = 0; i < n; i++) line += (i ? 'L' : 'M') + X(i).toFixed(1) + ' ' + Y(d[i]).toFixed(1);
    var s = '<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" height="' + H + '" preserveAspectRatio="none">';
    s += '<defs><linearGradient id="sg' + u + '" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0%" stop-color="' + o.color + '" stop-opacity="0.32"/>' +
      '<stop offset="100%" stop-color="' + o.color + '" stop-opacity="0"/></linearGradient></defs>';
    if (o.fill) s += '<path d="' + line + 'L' + X(n - 1).toFixed(1) + ' ' + H + 'L' + X(0).toFixed(1) + ' ' + H + 'Z" fill="url(#sg' + u + ')"/>';
    s += '<path d="' + line + '" fill="none" stroke="' + o.color + '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ' +
      'stroke-dasharray="900" stroke-dashoffset="900"><animate attributeName="stroke-dashoffset" from="900" to="0" dur="1.1s" fill="freeze"/></path>';
    if (o.dots) d.forEach(function (v, i) { s += '<circle cx="' + X(i).toFixed(1) + '" cy="' + Y(v).toFixed(1) + '" r="2.6" fill="' + o.color + '"/>'; });
    s += '<circle cx="' + X(n - 1).toFixed(1) + '" cy="' + Y(d[n - 1]).toFixed(1) + '" r="4" fill="' + o.color + '"/>';
    s += '</svg>';
    return s;
  }

  /* ============================================================
     4. 环形进度
     ============================================================ */
  function ring(opts) {
    var o = Object.assign({ value: 70, max: 100, size: 92, thickness: 7, color: 'var(--h)', label: '', unit: '' }, opts || {});
    var s0 = o.size, r = (s0 - o.thickness) / 2, c = 2 * Math.PI * r;
    var off = c * (1 - Math.min(o.value / o.max, 1));
    var s = '<svg viewBox="0 0 ' + s0 + ' ' + s0 + '" width="' + s0 + '" height="' + s0 + '">';
    s += '<circle cx="' + s0 / 2 + '" cy="' + s0 / 2 + '" r="' + r + '" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="' + o.thickness + '"/>';
    s += '<circle cx="' + s0 / 2 + '" cy="' + s0 / 2 + '" r="' + r + '" fill="none" stroke="' + o.color + '" stroke-width="' + o.thickness +
      '" stroke-linecap="round" stroke-dasharray="' + c.toFixed(1) + '" stroke-dashoffset="' + c.toFixed(1) + '" transform="rotate(-90 ' + s0 / 2 + ' ' + s0 / 2 + ')">' +
      '<animate attributeName="stroke-dashoffset" to="' + off.toFixed(1) + '" dur="1s" fill="freeze"/></circle>';
    s += '<text x="' + s0 / 2 + '" y="' + (s0 / 2 + 1) + '" text-anchor="middle" font-size="' + (s0 * 0.28) + '" font-weight="600" fill="#E9EEF9" font-family="Bahnschrift,system-ui">' + o.value + '</text>';
    if (o.label) s += '<text x="' + s0 / 2 + '" y="' + (s0 / 2 + s0 * 0.2) + '" text-anchor="middle" font-size="' + (s0 * 0.11) + '" fill="#7F8DA7">' + esc(o.label) + '</text>';
    s += '</svg>';
    return s;
  }

  /* ============================================================
     5. 多段环形（资产配置）
     ============================================================ */
  function donut(opts) {
    var o = Object.assign({ segments: [], size: 116, thickness: 15 }, opts || {});
    var s0 = o.size, r = (s0 - o.thickness) / 2, c = 2 * Math.PI * r;
    var total = o.segments.reduce(function (a, b) { return a + b.value; }, 0) || 1;
    var acc = 0;
    var s = '<svg viewBox="0 0 ' + s0 + ' ' + s0 + '" width="' + s0 + '" height="' + s0 + '">';
    s += '<circle cx="' + s0 / 2 + '" cy="' + s0 / 2 + '" r="' + r + '" fill="none" stroke="rgba(255,255,255,0.06)" stroke-width="' + o.thickness + '"/>';
    o.segments.forEach(function (g) {
      var len = c * g.value / total;
      s += '<circle cx="' + s0 / 2 + '" cy="' + s0 / 2 + '" r="' + r + '" fill="none" stroke="' + g.color + '" stroke-width="' + o.thickness +
        '" stroke-dasharray="' + (len - 2).toFixed(1) + ' ' + (c - len + 2).toFixed(1) + '" stroke-dashoffset="' + (-acc).toFixed(1) +
        '" transform="rotate(-90 ' + s0 / 2 + ' ' + s0 / 2 + ')" stroke-linecap="round"/>';
      acc += len;
    });
    s += '</svg>';
    return s;
  }

  /* ============================================================
     6. 水平条（贡献度 / 归因）
     ============================================================ */
  function hbars(opts) {
    var o = Object.assign({ items: [] }, opts || {});
    var html = '';
    o.items.forEach(function (it) {
      var pct = Math.min(100, it.value / (it.max || 100) * 100);
      html += '<div style="margin-bottom:11px">' +
        '<div class="row between" style="margin-bottom:6px">' +
        '<span style="font-size:11.5px;color:var(--text-2)">' + esc(it.label) + '</span>' +
        '<span class="num" style="font-size:11.5px;color:' + (it.color || 'var(--muted)') + '">' + esc(it.text != null ? it.text : it.value + '%') + '</span>' +
        '</div>' +
        '<div class="bar-track"><div class="bar-fill" style="width:' + pct.toFixed(1) + '%;background:linear-gradient(90deg,' +
        (it.color || 'var(--h)') + '66,' + (it.color || 'var(--h)') + ')"></div></div>' +
        (it.sub ? '<div style="font-size:10px;color:var(--muted-2);margin-top:5px">' + esc(it.sub) + '</div>' : '') +
        '</div>';
    });
    return html;
  }

  /* ============================================================
     7. 柱状对比（生物年龄 / 器官年龄）
     ============================================================ */
  function vbars(opts) {
    var o = Object.assign({ items: [], h: 96 }, opts || {});
    var max = Math.max.apply(null, o.items.map(function (i) { return i.value; })) * 1.18;
    var html = '<div class="row" style="align-items:flex-end;gap:10px;height:' + o.h + 'px">';
    o.items.forEach(function (it) {
      var pct = it.value / max * 100;
      html += '<div style="flex:1;text-align:center;display:flex;flex-direction:column;justify-content:flex-end;height:100%">' +
        '<div class="num" style="font-size:12px;font-weight:600;color:' + (it.color || 'var(--text)') + ';margin-bottom:5px">' + it.value +
        '<s style="text-decoration:none;font-size:9px;color:var(--muted-2)">' + (it.unit || '') + '</s></div>' +
        '<div style="height:' + pct.toFixed(1) + '%;background:linear-gradient(180deg,' + (it.color || 'var(--h)') + ',rgba(255,255,255,0.06));border-radius:7px 7px 3px 3px;min-height:4px"></div>' +
        '<div style="font-size:9.5px;color:var(--muted-2);margin-top:6px">' + esc(it.label) + '</div>' +
        '</div>';
    });
    html += '</div>';
    return html;
  }

  /* ============================================================
     8. 传导链路（健康→企业→资产）
     ============================================================ */
  function chain(opts) {
    var o = Object.assign({ nodes: [], risk: 0 }, opts || {});
    var html = '<div style="display:flex;align-items:center;gap:0">';
    o.nodes.forEach(function (nd, i) {
      html += '<div style="flex:1;text-align:center">' +
        '<div style="margin:0 auto;width:52px;height:52px;border-radius:16px;display:flex;align-items:center;justify-content:center;' +
        'background:' + nd.bg + ';border:1px solid ' + nd.bd + ';font-size:18px;color:' + nd.color + '">' + nd.icon + '</div>' +
        '<div style="font-size:10.5px;color:var(--text-2);margin-top:7px;line-height:1.35">' + esc(nd.label) + '</div>' +
        '<div class="num" style="font-size:12px;font-weight:600;color:' + nd.color + ';margin-top:3px">' + esc(nd.value) + '</div>' +
        '</div>';
      if (i < o.nodes.length - 1) {
        html += '<div style="width:34px;flex:0 0 auto;text-align:center;padding-bottom:30px">' +
          '<svg viewBox="0 0 34 14" width="34" height="14"><path d="M0 7h24" stroke="rgba(255,255,255,0.22)" stroke-width="1.4" stroke-dasharray="4 3"/>' +
          '<path d="M24 3l5 4-5 4z" fill="rgba(255,255,255,0.32)"/></svg></div>';
      }
    });
    html += '</div>';
    return html;
  }

  root.HW3 = root.HW3 || {};
  root.HW3.charts = {
    helix: helix, radar: radar, spark: spark, ring: ring,
    donut: donut, hbars: hbars, vbars: vbars, chain: chain
  };
})(window);
